import type {
  Dance,
  Performance,
  PerformanceDancerIndex,
  Recital,
  Studio
} from "@recital-master/shared";
import { danceStyles } from "@recital-master/shared";

/** Everything the app knows about published recitals. Matches the ingest
 * pipeline's catalog.json. */
export type Catalog = {
  studios: Studio[];
  recitals: Recital[];
  performances: Performance[];
  dances: Record<string, Dance[]>;
  dancerIndexes: Record<string, PerformanceDancerIndex>;
};

export const DEV_MEDIA_BASE =
  process.env.EXPO_PUBLIC_DEMO_MEDIA_BASE ??
  process.env.EXPO_PUBLIC_DEV_MEDIA_BASE ??
  "https://media.recitalmaster.com/demo";

/** Operator-authored performance identity in display order. */
export function selectPerformanceFeaturedDances(
  performance: Performance,
  dances: Dance[],
  limit = 3
): Dance[] {
  const selected = performance.featuredDanceIds
    .map((danceId) => dances.find((dance) => dance.danceId === danceId))
    .filter((dance): dance is Dance => Boolean(dance));
  const selectedIds = new Set(selected.map((dance) => dance.danceId));
  return [
    ...selected,
    ...dances.filter((dance) => !selectedIds.has(dance.danceId))
  ].slice(0, limit);
}

/** The first usable featured still is the Performance card/page's shared mood. */
export function performanceLeadArtworkKey(
  performance: Performance,
  dances: Dance[]
): string | null {
  return selectPerformanceFeaturedDances(performance, dances)
    .find((dance) => dance.thumbPath)?.thumbPath ?? null;
}

/** Accepts the canonical catalog shape and normalizes authored style values. */
export function normalizeCatalog(raw: unknown): Catalog {
  const data = raw as Catalog;
  const studios = data.studios ?? [];
  return {
    studios,
    recitals: (data.recitals ?? []).map((recital) => {
      if (!recital.studioSlug) {
        throw new Error(`Recital ${recital.recitalId} is missing its canonical studioSlug.`);
      }
      return {
        ...recital,
        artColors: recital.artColors ?? [],
        intro: recital.intro ? normalizeDance(recital.intro) : null
      };
    }),
    performances: data.performances,
    dances: Object.fromEntries(
      Object.entries(data.dances).map(([performanceId, dances]) => [
        performanceId,
        dances.map(normalizeDance)
      ])
    ),
    dancerIndexes: data.dancerIndexes
  };
}

/** Trims and deduplicates the canonical ordered style set. */
export function normalizeDance(dance: Dance): Dance {
  return {
    ...dance,
    styles: danceStyles(dance)
  };
}

export async function loadDevCatalog(): Promise<Catalog> {
  const response = await fetch(`${DEV_MEDIA_BASE}/catalog.json`);
  if (!response.ok) {
    throw new Error(`Dev catalog fetch failed: ${response.status}`);
  }
  return normalizeCatalog(await response.json());
}
